import { useState, useEffect } from 'react';
import { db, queueSync } from '../db';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, TrendingDown, TrendingUp, AlertCircle, Pencil, Trash2, Utensils, Hotel, Car, Plane, Shield } from 'lucide-react';
import AddExpenseModal from '../components/AddExpenseModal';
import PaywallModal from '../components/PaywallModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import AISuggestionsModal from '../components/AISuggestionsModal';
import RunwayProjection from '../components/RunwayProjection';
import RunwayModal from '../components/RunwayModal';
import QuickConverter from '../components/QuickConverter';
import ScamAlertsModal from '../components/ScamAlertsModal';
import EsimCard from '../components/EsimCard';
import IOUCard from '../components/IOUCard';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../utils/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TodayScreen = ({ isPremium, expenseCount, setExpenseCount }) => {
  const { t } = useTranslation();
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [todayExpenses, setTodayExpenses] = useState([]);
  const [dailyBudget, setDailyBudget] = useState(0);
  const [spent, setSpent] = useState(0);
  const [totalSpent, setTotalSpent] = useState(0);
  const [daysElapsed, setDaysElapsed] = useState(1);
  const [burnRate, setBurnRate] = useState('green');
  const [editingExpense, setEditingExpense] = useState(null);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [suggestionType, setSuggestionType] = useState('food');
  const [userLocation, setUserLocation] = useState('Current Location');
  const [showRunwayModal, setShowRunwayModal] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [prefilledExpense, setPrefilledExpense] = useState(null);
  const [showScamAlerts, setShowScamAlerts] = useState(false);
  const [destination, setDestination] = useState('');
  const [iouRefreshKey, setIouRefreshKey] = useState(0);

  useEffect(() => {
    loadTodayData();
    loadSettings();
    loadUserLocation();
    loadTripStats();
    loadBaseCurrency();
    loadDestination();
  }, []);

  const loadDestination = async () => {
    try {
      const dest = await db.settings.get('destination');
      if (dest?.value) {
        setDestination(dest.value);
      }
    } catch (error) {
      console.error('Failed to load destination:', error);
    }
  };

  const loadBaseCurrency = async () => {
    try {
      const currencySetting = await db.settings.get('currency');
      if (currencySetting?.value) {
        setBaseCurrency(currencySetting.value);
      }
    } catch (error) {
      console.error('Failed to load currency:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const budgetSetting = await db.settings.get('daily_budget');
      if (budgetSetting) {
        setDailyBudget(parseFloat(budgetSetting.value));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadUserLocation = async () => {
    try {
      const destinationSetting = await db.settings.get('destination');
      if (destinationSetting) {
        setUserLocation(destinationSetting.value);
      }
    } catch (error) {
      console.error('Failed to load location:', error);
    }
  };

  const loadTripStats = async () => {
    try {
      // Get current trip ID
      const currentTripIdSetting = await db.settings.get('current_trip_id');
      const currentTripId = currentTripIdSetting?.value || null;
      
      // Get start date (using correct key from onboarding)
      const startDateSetting = await db.settings.get('trip_start_date');
      const startDate = startDateSetting?.value ? new Date(startDateSetting.value) : new Date();
      
      // Calculate days elapsed
      const today = new Date();
      const diffTime = Math.abs(today - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) || 1;
      setDaysElapsed(diffDays);
      
      // Get all expenses for current trip (not archived, not deleted)
      const allExpenses = await db.expenses
        .filter(expense => {
          return !expense.deleted && 
                 !expense.archived &&
                 (!expense.trip_id || expense.trip_id === currentTripId);
        })
        .toArray();
      
      // Calculate total spent across all days
      const total = allExpenses.reduce((sum, exp) => sum + exp.amount, 0);
      setTotalSpent(total);
    } catch (error) {
      console.error('Failed to load trip stats:', error);
    }
  };

  const handleAISuggestionClick = (type) => {
    if (!isPremium) {
      setShowPaywall(true);
      return;
    }
    
    setSuggestionType(type);
    setShowAISuggestions(true);
  };

  const loadTodayData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Get current trip ID
      const currentTripIdSetting = await db.settings.get('current_trip_id');
      const currentTripId = currentTripIdSetting?.value || null;
      
      // Get expenses for today that belong to current trip (not archived)
      const expenses = await db.expenses
        .where('date')
        .equals(today)
        .and(expense => {
          // Show only non-deleted expenses that:
          // 1. Have no trip_id (created before trip tracking) OR
          // 2. Have the current trip_id OR
          // 3. Are not archived
          return !expense.deleted && 
                 (!expense.archived || expense.trip_id === currentTripId);
        })
        .toArray();

      setTodayExpenses(expenses);
      
      // Calculate total - all expenses are already in base currency
      const totalSpent = expenses.reduce((sum, exp) => sum + exp.amount, 0);
      setSpent(totalSpent);
      
      // Also refresh trip stats
      loadTripStats();

      // Calculate burn rate
      if (dailyBudget > 0) {
        const percentage = (totalSpent / dailyBudget) * 100;
        if (percentage < 70) setBurnRate('green');
        else if (percentage < 90) setBurnRate('yellow');
        else setBurnRate('red');
      }
    } catch (error) {
      console.error('Failed to load today expenses:', error);
    }
  };

  const handleAddExpense = async (expense) => {
    try {
      // Get base currency from settings
      const baseCurrencySetting = await db.settings.get('currency');
      const baseCurrency = baseCurrencySetting?.value || 'USD';
      
      // Get current trip ID
      const currentTripIdSetting = await db.settings.get('current_trip_id');
      const currentTripId = currentTripIdSetting?.value || null;
      
      // Save the currency used for this expense as the preferred expense currency
      await db.settings.put({ key: 'last_expense_currency', value: expense.currency });
      
      // Convert to base currency immediately
      let finalAmount = expense.amount;
      let conversionNote = '';
      
      if (expense.currency !== baseCurrency) {
        try {
          // Fetch exchange rate
          const expenseRateResponse = await axios.get(`${API}/exchange-rates/${expense.currency}`);
          const baseRateResponse = await axios.get(`${API}/exchange-rates/${baseCurrency}`);
          
          const expenseRate = expenseRateResponse.data.rate;
          const baseRate = baseRateResponse.data.rate;
          
          finalAmount = (expense.amount / expenseRate) * baseRate;
          conversionNote = `Converted from ${expense.amount.toFixed(2)} ${expense.currency}`;
        } catch (error) {
          console.log('Using offline conversion rates');
          // Use cached rates from IndexedDB
          const expenseCurrencyRate = await db.exchangeRates.get(expense.currency);
          const baseCurrencyRate = await db.exchangeRates.get(baseCurrency);
          
          if (expenseCurrencyRate && baseCurrencyRate) {
            const expenseRate = expenseCurrencyRate.rate;
            const baseRate = baseCurrencyRate.rate;
            finalAmount = (expense.amount / expenseRate) * baseRate;
            conversionNote = `Converted from ${expense.amount.toFixed(2)} ${expense.currency}`;
          }
        }
      }

      const convertedExpense = {
        amount: finalAmount,
        currency: baseCurrency,
        category: expense.category,
        note: conversionNote ? `${expense.note || ''}${expense.note ? ' | ' : ''}${conversionNote}`.trim() : expense.note,
        date: expense.date,
        trip_id: currentTripId,
        archived: false,
        synced: false,
        deleted: false,
        created_at: new Date().toISOString()
      };

      // Save to IndexedDB and get the generated ID
      const id = await db.expenses.add(convertedExpense);
      
      // Update the expense with its ID
      await db.expenses.update(id, { expense_id: id });

      // Queue for sync
      await queueSync('create', 'expenses', { ...convertedExpense, expense_id: id });

      // Sync immediately if online
      if (navigator.onLine) {
        try {
          await axios.post(`${API}/expenses`, { ...convertedExpense, expense_id: id });
        } catch (error) {
          console.log('Will sync when online');
        }
      }

      setExpenseCount(expenseCount + 1);
      loadTodayData();
      setShowAddExpense(false);
      setIouRefreshKey(prev => prev + 1); // Refresh IOUCard
      
      // Show conversion confirmation
      if (expense.currency !== baseCurrency) {
        toast.success(`${expense.amount} ${expense.currency} = ${finalAmount.toFixed(2)} ${baseCurrency}`);
      } else {
        toast.success('Expense added');
      }

      // Check if paywall should trigger
      if (!isPremium && expenseCount + 1 >= 10) {
        setTimeout(() => setShowPaywall(true), 1000);
      }
    } catch (error) {
      console.error('Failed to add expense:', error);
      toast.error('Failed to add expense');
    }
  };

  const handleShowRunway = () => {
    setShowRunwayModal(true);
  };

  const handleRunwayUpgrade = () => {
    setShowRunwayModal(false);
    setShowPaywall(true);
  };

  const handleDeleteClick = (expense) => {
    console.log('Delete clicked for expense:', expense);
    setExpenseToDelete(expense);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!expenseToDelete) {
      toast.error('Cannot delete: No expense selected');
      return;
    }

    const expenseId = expenseToDelete.id || expenseToDelete.expense_id;
    
    if (!expenseId) {
      toast.error('Cannot delete: Invalid expense ID');
      setShowDeleteConfirm(false);
      setExpenseToDelete(null);
      return;
    }

    try {
      console.log('Deleting expense with ID:', expenseId);
      
      // Delete from IndexedDB
      await db.expenses.delete(expenseId);
      console.log('Deleted from IndexedDB');
      
      // Queue for sync
      await queueSync('delete', 'expenses', { id: expenseId });

      // Sync immediately if online
      if (navigator.onLine) {
        try {
          await axios.delete(`${API}/expenses/${expenseId}`);
          console.log('Synced delete to backend');
        } catch (error) {
          console.log('Will sync when online');
        }
      }

      // Close modal and clear state
      setShowDeleteConfirm(false);
      setExpenseToDelete(null);
      
      // Reload data
      await loadTodayData();
      
      toast.success('Expense deleted');
    } catch (error) {
      console.error('Failed to delete expense:', error);
      toast.error('Failed to delete expense');
      setShowDeleteConfirm(false);
      setExpenseToDelete(null);
    }
  };

  const handleEditExpense = (expense) => {
    // Extract original amount from note if it was converted
    let originalAmount = expense.amount;
    let originalCurrency = expense.currency;
    
    // Check if note contains conversion info
    if (expense.note && expense.note.includes('Converted from')) {
      const match = expense.note.match(/Converted from ([\d.]+) ([A-Z]{3})/);
      if (match) {
        originalAmount = parseFloat(match[1]);
        originalCurrency = match[2];
      }
    }

    setEditingExpense({
      ...expense,
      original_amount: originalAmount,
      original_currency: originalCurrency
    });
    setShowAddExpense(true);
  };

  const handleUpdateExpense = async (updatedExpense) => {
    try {
      if (!editingExpense || !editingExpense.id) {
        toast.error('Cannot update: Invalid expense');
        return;
      }

      // Save the currency used as the preferred expense currency
      await db.settings.put({ key: 'last_expense_currency', value: updatedExpense.currency });

      // Get base currency
      const baseCurrencySetting = await db.settings.get('currency');
      const baseCurrency = baseCurrencySetting?.value || 'USD';
      
      // Convert to base currency immediately
      let finalAmount = updatedExpense.amount;
      let conversionNote = '';
      
      if (updatedExpense.currency !== baseCurrency) {
        try {
          const expenseRateResponse = await axios.get(`${API}/exchange-rates/${updatedExpense.currency}`);
          const baseRateResponse = await axios.get(`${API}/exchange-rates/${baseCurrency}`);
          
          const expenseRate = expenseRateResponse.data.rate;
          const baseRate = baseRateResponse.data.rate;
          
          finalAmount = (updatedExpense.amount / expenseRate) * baseRate;
          conversionNote = `Converted from ${updatedExpense.amount.toFixed(2)} ${updatedExpense.currency}`;
        } catch (error) {
          // Try offline rates
          const expenseCurrencyRate = await db.exchangeRates.get(updatedExpense.currency);
          const baseCurrencyRate = await db.exchangeRates.get(baseCurrency);
          
          if (expenseCurrencyRate && baseCurrencyRate) {
            finalAmount = (updatedExpense.amount / expenseCurrencyRate.rate) * baseCurrencyRate.rate;
            conversionNote = `Converted from ${updatedExpense.amount.toFixed(2)} ${updatedExpense.currency}`;
          }
        }
      }

      // Remove old conversion note from the note field
      let cleanNote = updatedExpense.note || '';
      if (cleanNote.includes('Converted from')) {
        cleanNote = cleanNote.split('|')[0].trim();
      }

      const updatedData = {
        amount: finalAmount,
        currency: baseCurrency,
        category: updatedExpense.category,
        note: conversionNote ? `${cleanNote}${cleanNote ? ' | ' : ''}${conversionNote}`.trim() : cleanNote,
        date: updatedExpense.date,
        updated_at: new Date().toISOString()
      };

      // Update in IndexedDB
      await db.expenses.update(editingExpense.id, updatedData);
      
      // Queue for sync
      const expenseIdForSync = editingExpense.expense_id || editingExpense.id;
      await queueSync('update', 'expenses', { id: expenseIdForSync, ...updatedData });

      // Sync immediately if online
      if (navigator.onLine) {
        try {
          await axios.put(`${API}/expenses/${expenseIdForSync}`, updatedData);
        } catch (error) {
          console.log('Will sync when online');
        }
      }

      loadTodayData();
      setShowAddExpense(false);
      setEditingExpense(null);
      toast.success('Expense updated');
    } catch (error) {
      console.error('Failed to update expense:', error);
      toast.error('Failed to update expense');
    }
  };

  const remaining = dailyBudget - spent;
  const runwayDays = dailyBudget > 0 && spent > 0 ? Math.floor((dailyBudget * 30) / spent) : 0;

  return (
    <div className="p-4 space-y-6 animate-in pb-24">
      {/* Header */}
      <div className="pt-6 flex flex-col items-center">
        <div className="flex items-center gap-3">
          <img src="/logo-icon.png" alt="" className="h-12 w-auto" data-testid="app-logo" />
          <h1 className="text-2xl font-heading font-bold text-foreground tracking-tight">
            Wanderer's<br /><span className="text-primary">Wallet</span>
          </h1>
        </div>
        <p className="text-muted-foreground mt-2 text-sm">Protecting your ability to keep traveling</p>
      </div>

      {/* Budget Overview - Bento Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Daily Budget Card */}
        <Card data-testid="daily-budget-card" className="bg-card border-border">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-sm text-muted-foreground font-normal">Daily Budget</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-2xl font-heading font-bold text-foreground">
              ${dailyBudget.toFixed(0)}
            </p>
          </CardContent>
        </Card>

        {/* Spent Today Card */}
        <Card data-testid="spent-today-card" className="bg-card border-border">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-sm text-muted-foreground font-normal">Spent Today</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-2xl font-heading font-bold text-foreground">
              ${spent.toFixed(0)}
            </p>
          </CardContent>
        </Card>

        {/* Remaining Card */}
        <Card data-testid="remaining-card" className="bg-card border-border">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-sm text-muted-foreground font-normal">Remaining</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className={`text-2xl font-heading font-bold ${
              remaining >= 0 ? 'text-primary' : 'text-destructive'
            }`}>
              ${Math.abs(remaining).toFixed(0)}
            </p>
          </CardContent>
        </Card>

        {/* Burn Rate Card */}
        <Card data-testid="burn-rate-card" className="bg-card border-border">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-sm text-muted-foreground font-normal">Burn Rate</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center gap-2">
            {burnRate === 'green' && <TrendingDown className="w-6 h-6 text-primary" />}
            {burnRate === 'yellow' && <TrendingUp className="w-6 h-6 text-yellow-500" />}
            {burnRate === 'red' && <AlertCircle className="w-6 h-6 text-destructive" />}
            <span className="text-lg font-semibold capitalize">{burnRate}</span>
          </CardContent>
        </Card>
      </div>

      {/* Understand Your Runway - Prominent CTA */}
      <Button
        data-testid="understand-runway-button"
        onClick={handleShowRunway}
        className="w-full h-16 rounded-2xl bg-gradient-to-r from-primary/90 to-primary text-primary-foreground font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
      >
        <Plane className="w-5 h-5" />
        <div className="text-left">
          <p className="text-base font-semibold">Understand Your Runway</p>
          <p className="text-xs opacity-80">How long can you keep traveling?</p>
        </div>
        {!isPremium && (
          <span className="ml-auto text-xs bg-white/20 px-2 py-1 rounded-full">
            Preview
          </span>
        )}
      </Button>

      {/* Premium Runway Projection Component */}
      {isPremium && <RunwayProjection dailyBudget={dailyBudget} spent={spent} />}

      {/* Quick Currency Converter - Main Feature */}
      <QuickConverter 
        baseCurrency={baseCurrency}
        onAddExpense={(data) => {
          setPrefilledExpense({
            amount: data.amount,
            currency: data.currency
          });
          setShowAddExpense(true);
        }}
      />

      {/* Add Expense Button */}
      <Button
        data-testid="add-expense-button"
        onClick={() => {
          setPrefilledExpense(null);
          setShowAddExpense(true);
        }}
        className="w-full h-14 rounded-full bg-primary text-primary-foreground font-semibold shadow-primary hover:shadow-primary-hover transition-all duration-300"
      >
        <Plus className="w-5 h-5 mr-2" />
        {t('add_expense')}
      </Button>

      {/* IOUs - Friends Owe You */}
      <IOUCard key={iouRefreshKey} />

      {/* eSIM Card */}
      <EsimCard destination={destination} />

      {/* Scam Alerts Card */}
      {destination && (
        <Card 
          data-testid="scam-alerts-card"
          className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30 cursor-pointer hover:border-yellow-500/50 transition-colors"
          onClick={() => setShowScamAlerts(true)}
        >
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">{t('stay_safe_in', { destination })}</h3>
              <p className="text-sm text-muted-foreground">{t('view_safety_tips')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Suggestions - Premium Feature */}
      <Card data-testid="ai-suggestions-card" className="bg-gradient-to-br from-card to-secondary/50 border-primary/20">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            AI Budget Recommendations
            {!isPremium && (
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                Premium
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Get top-rated places within your budget
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-3 gap-3">
          <Button
            data-testid="ai-food-button"
            onClick={() => handleAISuggestionClick('food')}
            variant="outline"
            className={`h-20 flex flex-col gap-2 border-border ${!isPremium ? 'opacity-70' : ''}`}
          >
            <Utensils className="w-5 h-5 text-primary" />
            <span className="text-xs">Food</span>
          </Button>
          
          <Button
            data-testid="ai-lodging-button"
            onClick={() => handleAISuggestionClick('lodging')}
            variant="outline"
            className={`h-20 flex flex-col gap-2 border-border ${!isPremium ? 'opacity-70' : ''}`}
          >
            <Hotel className="w-5 h-5 text-primary" />
            <span className="text-xs">Lodging</span>
          </Button>
          
          <Button
            data-testid="ai-transport-button"
            onClick={() => handleAISuggestionClick('transport')}
            variant="outline"
            className={`h-20 flex flex-col gap-2 border-border ${!isPremium ? 'opacity-70' : ''}`}
          >
            <Car className="w-5 h-5 text-primary" />
            <span className="text-xs">Transport</span>
          </Button>
        </CardContent>
      </Card>

      {/* Today's Expenses List */}
      {todayExpenses.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-heading font-semibold">Today's Expenses</h2>
          {todayExpenses.map((expense, index) => (
            <Card key={index} data-testid={`expense-item-${index}`} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{expense.category}</p>
                    {expense.note && (
                      <p className="text-sm text-muted-foreground mt-1">{expense.note}</p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-lg font-bold text-foreground">
                      ${expense.amount.toFixed(2)}
                    </p>
                    {expense.currency && (
                      <p className="text-xs text-muted-foreground">{expense.currency}</p>
                    )}
                  </div>
                </div>
                
                {/* Edit and Delete Buttons */}
                <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                  <Button
                    data-testid={`edit-expense-${index}`}
                    onClick={() => handleEditExpense(expense)}
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-8 text-xs"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    data-testid={`delete-expense-${index}`}
                    onClick={() => handleDeleteClick(expense)}
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-8 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <AddExpenseModal
        open={showAddExpense}
        onClose={() => {
          setShowAddExpense(false);
          setEditingExpense(null);
          setPrefilledExpense(null);
        }}
        onAdd={editingExpense ? handleUpdateExpense : handleAddExpense}
        editMode={!!editingExpense}
        initialData={editingExpense ? {
          amount: editingExpense.original_amount || editingExpense.amount,
          currency: editingExpense.original_currency || editingExpense.currency,
          category: editingExpense.category,
          note: editingExpense.note ? editingExpense.note.split('|')[0].trim() : '',
          date: editingExpense.date
        } : prefilledExpense}
      />

      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
      />

      <DeleteConfirmModal
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setExpenseToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        expenseDetails={expenseToDelete}
      />

      <AISuggestionsModal
        open={showAISuggestions}
        onClose={() => setShowAISuggestions(false)}
        type={suggestionType}
        dailyBudget={dailyBudget}
        location={userLocation}
      />

      <RunwayModal
        open={showRunwayModal}
        onClose={() => setShowRunwayModal(false)}
        isPremium={isPremium}
        onUpgrade={handleRunwayUpgrade}
        dailyBudget={dailyBudget}
        totalSpent={totalSpent}
        daysElapsed={daysElapsed}
      />

      <ScamAlertsModal
        open={showScamAlerts}
        onClose={() => setShowScamAlerts(false)}
        destination={destination}
      />
    </div>
  );
};

export default TodayScreen;